import ApiService from '../../services/apiService';
import URL from '../../urls/Urls';
import type {
  Location,
  BillingAddress,
  Poc,
  Client,
  ClientDetailData,
  PoRecord,
  InvoiceRecord,
  SkuLine,
  StatusKey,
} from './types';

/* ============================================================
   DATA LAYER
   Replace the two fetch* functions at the bottom with real calls
   to your backend. Shapes are documented in types.ts so the rest
   of the UI does not need to change when you swap them.
   ============================================================ */

export const LOCATION_POOL: Location[] = [
  {
    id: 'LOC-1',
    name: 'Main Warehouse',
    address: '4820 Industrial Pkwy, Dallas, TX 75207',
  },
  {
    id: 'LOC-2',
    name: 'East Distribution Center',
    address: '1190 Harbor Rd, Newark, NJ 07114',
  },
  {
    id: 'LOC-3',
    name: 'West Fulfillment Hub',
    address: '225 Commerce Way, Reno, NV 89502',
  },
  {
    id: 'LOC-4',
    name: 'Midwest Cross-Dock',
    address: '77 Freight Ln, Columbus, OH 43219',
  },
  {
    id: 'LOC-5',
    name: 'Gulf Coast Depot',
    address: '3300 Port Access Rd, Houston, TX 77029',
  },
  {
    id: 'LOC-6',
    name: 'Pacific Northwest Yard',
    address: '615 Alder St, Tacoma, WA 98421',
  },
  {
    id: 'LOC-7',
    name: 'Southeast Regional DC',
    address: '4110 Peachtree Industrial Blvd, Atlanta, GA 30341',
  },
  {
    id: 'LOC-8',
    name: 'Rocky Mountain Hub',
    address: '980 Airport Rd, Denver, CO 80249',
  },
  {
    id: 'LOC-9',
    name: 'Northeast Satellite',
    address: '22 Depot St, Hartford, CT 06103',
  },
  {
    id: 'LOC-10',
    name: 'Central Valley Annex',
    address: '510 Grower Ave, Fresno, CA 93706',
  },
];

export const BILLING_ADDRESS_POOL: BillingAddress[] = [
  {
    id: 'BILL-1',
    label: 'Primary',
    address: '900 Ledger Ave, Suite 400, Chicago, IL 60607',
  },
  {
    id: 'BILL-2',
    label: 'Remit-to',
    address: 'PO Box 4471, Chicago, IL 60680',
  },
  {
    id: 'BILL-3',
    label: 'Regional office',
    address: '58 Fiscal Row, Columbus, OH 43215',
  },
  {
    id: 'BILL-4',
    label: 'Parent company',
    address: '1200 Holdings Plaza, New York, NY 10016',
  },
];

interface SkuBase {
  code: string;
  description: string;
  basePrice: number;
}

export const SKU_POOL: SkuBase[] = [
  {
    code: 'SKU-1001',
    description: 'Insulated shipping carton, 12x12x8',
    basePrice: 4.5,
  },
  {
    code: 'SKU-1002',
    description: 'Pallet wrap, 20in stretch film',
    basePrice: 12.8,
  },
  { code: 'SKU-1003', description: 'Corrugated divider set', basePrice: 6.1 },
  { code: 'SKU-1004', description: 'Barcode label roll, 4x6', basePrice: 3.25 },
  {
    code: 'SKU-1005',
    description: 'Tamper-evident seal, 1000ct',
    basePrice: 9.4,
  },
];

export const LOCATION_PRICE_MULTIPLIER: Record<string, number> =
  LOCATION_POOL.reduce(
    (m, loc, i) => {
      m[loc.id] = 1 + ((i % 4) - 1.5) * 0.06;
      return m;
    },
    {} as Record<string, number>
  );

interface PocBase {
  name: string;
  title: string;
  phone: string;
}

export const POC_POOL: PocBase[] = [
  {
    name: 'Dana Whitfield',
    title: 'Procurement Manager',
    phone: '(214) 555-0132',
  },
  {
    name: 'Marcus Chen',
    title: 'Accounts Payable Lead',
    phone: '(973) 555-0187',
  },
  {
    name: 'Priya Anand',
    title: 'Operations Director',
    phone: '(775) 555-0194',
  },
  {
    name: 'Owen Bradley',
    title: 'Warehouse Supervisor',
    phone: '(614) 555-0163',
  },
  {
    name: 'Renata Silva',
    title: 'Logistics Coordinator',
    phone: '(713) 555-0148',
  },
  { name: 'Tobias Lund', title: 'Inventory Analyst', phone: '(253) 555-0175' },
  { name: 'Grace Okafor', title: 'Regional Buyer', phone: '(404) 555-0121' },
  {
    name: 'Levi Marsh',
    title: 'Fleet & Receiving Lead',
    phone: '(303) 555-0159',
  },
];

const apiClient = ApiService().client;

function makeMockClientDetail(client: Client): ClientDetailData {
  const locations = client.locations || [];
  const poCount = 1;

  const pos: PoRecord[] = Array.from({ length: poCount }).map((_, i) => {
    const location = locations[i % locations.length];
    const lineCount = 2 + (i % 3);
    const skuLines = Array.from({ length: lineCount }).map((_, s) => {
      const base = SKU_POOL[(i + s) % SKU_POOL.length];
      const unitPrice = +(
        base.basePrice * LOCATION_PRICE_MULTIPLIER[location.id]
      ).toFixed(2);
      const qty = 40 + s * 15 + i * 5;
      return {
        code: base.code,
        description: base.description,
        qty,
        unitPrice,
      };
    });
    const amount = skuLines.reduce((sum, s) => sum + s.qty * s.unitPrice, 0);
    const status: StatusKey = i % 3 === 0 ? 'closed' : 'open';
    return {
      id: `${client.id}-PO-${5000 + i}`,
      number: `PO-${5000 + i}`,
      date: `2026-0${(i % 6) + 1}-1${i % 9}`,
      status,
      amount,
      location,
      skus: skuLines,
    };
  });

  const invoices: InvoiceRecord[] = Array.from({
    length: 1,
  }).map((_, i) => {
    const po = pos[i % pos.length];
    const statusPool: StatusKey[] = ['paid', 'paid', 'pending', 'overdue'];
    const status: StatusKey =
      client.status === 'overdue' && i === 0
        ? 'overdue'
        : statusPool[i % statusPool.length];
    const amount = 1200 + i * 340 + (i % 3) * 90;
    return {
      id: `${client.id}-INV-${100 + i}`,
      number: `INV-${2026000 + i}`,
      date: `2026-0${(i % 6) + 1}-0${(i % 8) + 1}`,
      dueDate: `2026-0${((i + 1) % 6) + 1}-0${(i % 8) + 1}`,
      amount,
      status,
      poNumber: po.number,
      location: po.location,
      skus: po.skus,
    };
  });

  // Flatten SKU lines across POs, keeping location + price context.
  // The same code can appear multiple times here at different
  // locations/POs, each with its own qty and unit price.
  const skuLines: SkuLine[] = pos.flatMap((po) =>
    po.skus.map((s, idx) => ({
      id: `${po.id}-line-${idx}`,
      ...s,
      poNumber: po.number,
      location: po.location,
    }))
  );

  return { invoices, pos, skuLines };
}

// Swap point: GET /api/clients
export async function fetchClients(): Promise<Client[]> {
  const res = await apiClient.get(URL.ZOHO.CLIENT_LIST);
  return res.data.clients;
}

// Swap point: GET /api/clients/:id/detail
export async function fetchClientDetail(
  client: Client
): Promise<ClientDetailData> {
  await new Promise((r) => setTimeout(r, 350));
  return makeMockClientDetail(client);
}
